import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getScoringRules,
  deleteScoringRule,
  SCORING_ENTITY_TYPES,
  type LeadScoringRule,
  type LeadScoringRuleFilters,
} from '@/api/analyticsApi';
import { useAuth } from '@/hooks/useAuth';
import AnalyticsNav from './AnalyticsNav';

export default function ScoringRuleListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [entityTypeFilter, setEntityTypeFilter] = useState<string | undefined>();

  const canRead = hasPermission('analytics', 'read');
  const canWrite = hasPermission('analytics', 'write');
  const canDelete = hasPermission('analytics', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: LeadScoringRuleFilters = { ...params };
      if (entityTypeFilter) filters.entity_type = entityTypeFilter;
      return getScoringRules(filters);
    },
    [entityTypeFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('lead_scoring.confirm_delete_rule'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteScoringRule(id);
            message.success(t('lead_scoring.rule_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<LeadScoringRule> = useMemo(
    () => [
      {
        title: t('lead_scoring.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('lead_scoring.field.entity_type'),
        dataIndex: 'entity_type',
        key: 'entity_type',
        width: 120,
        render: (v: string) => (
          <Tag color="blue">{t(`lead_scoring.entity_type.${v}`, v)}</Tag>
        ),
      },
      {
        title: t('lead_scoring.field.field'),
        dataIndex: 'field',
        key: 'field',
        width: 130,
      },
      {
        title: t('lead_scoring.field.operator'),
        dataIndex: 'operator',
        key: 'operator',
        width: 120,
        render: (v: string) => t(`lead_scoring.operator.${v}`, v),
      },
      {
        title: t('lead_scoring.field.score'),
        dataIndex: 'score',
        key: 'score',
        width: 80,
        sorter: true,
        render: (v: number) => (
          <Tag color={v >= 0 ? 'green' : 'red'}>{v > 0 ? `+${v}` : v}</Tag>
        ),
      },
      {
        title: t('lead_scoring.field.priority'),
        dataIndex: 'priority',
        key: 'priority',
        width: 80,
        sorter: true,
      },
      {
        title: t('lead_scoring.field.is_active'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 80,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'default'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('lead_scoring.field.category'),
        dataIndex: 'category',
        key: 'category',
        width: 120,
        ellipsis: true,
        render: (v: string) => v || '-',
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 120,
        render: (_: unknown, record: LeadScoringRule) => (
          <Space size="small">
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/analytics/scoring-rules/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('lead_scoring.field.entity_type')}
        value={entityTypeFilter}
        onChange={(v) => { setEntityTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 160 }}
        options={SCORING_ENTITY_TYPES.map((et) => ({
          value: et,
          label: t(`lead_scoring.entity_type.${et}`),
        }))}
      />
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/analytics/scoring-rules/new')}
        >
          {t('lead_scoring.create_rule_title')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AnalyticsNav />
      <h2>{t('lead_scoring.rules_title')}</h2>
      <DataGrid<LeadScoringRule>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('lead_scoring.search_rules_placeholder')}
        defaultSortField="priority"
        defaultSortDescending={false}
        toolbar={toolbar}
      />
    </div>
  );
}
